import React, { FC, MouseEventHandler } from 'react';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import { useTranslations } from '../../../hooks/useTranslations';
import { useTheme } from '../../../hooks/useTheme';

export interface SkipToContentProps {
  targetId: string;
  /** If set to true, it's also required that the target has tabIndex={-1} set because .focus() is used as a part of the navigate-without-scroll technique */
  scrollToTarget?: boolean;
}

const SkipToContent: FC<SkipToContentProps> = ({
  targetId,
  scrollToTarget,
}) => {
  const { linksNoneDefault, linksNoneHover } = useTheme();
  const { skipToContent } = useTranslations();
  if (!skipToContent)
    throw new Error(
      'Translation key `skipToContent` has no value. Please add this translation',
    );
  const preventScrollOnNavigate: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      window.history.pushState(null, '', `#${targetId}`);
      targetElement.focus();
    }
  };

  return (
    <TextLink
      target={TextLinkTargetType.SELF}
      linkStyle={{
        sDefault: {
          ...linksNoneDefault,
          padding: '0.5rem 1rem',
          background: '#fff',
          opacity: 0,
          position: 'absolute',
          zIndex: 100, // Because the div below this element has zIndex: 99,
          top: '-9999px',
          left: '-9999px',
          transition: 'opacity 0.3s ease-in-out',
        },
        sHover: {
          ...linksNoneHover,
          background: '#fff',
        },
        sFocus: {
          top: '1rem',
          left: '50%',
          transform: 'translate(-50%, 0)',
          opacity: 1,
        },
      }}
      href={`#${targetId}`}
      onClick={scrollToTarget ? undefined : preventScrollOnNavigate}
    >
      {skipToContent}
    </TextLink>
  );
};

export default SkipToContent;
